import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import AppInput from '../../../base/input';
import CustomDropdown from '../../../base/custom-dropdown';
import AppTextArea from '../../../base/text-area';

import { isFutureDate } from '../../../../utils/dates.util';

import kybUpdateValidationSchemas from '../../../../constants/schemas/kyb-update';
import {
  useAuthStore,
  useBusinessStore,
  useOnboardingStore,
  useRateStore,
  useResourceStore,
} from '../../../../store';
import AppDatePicker from '../../../base/date-picker';
import toast from 'react-hot-toast';
import { IActiveCountry } from '../../../../models/rate.model';

const AboutBusiness = () => {
  const { getAllCountryStates } = useResourceStore();
  const { getActiveCountries, activeCountries: countries } = useRateStore();
  const { setBusinessType, businessInfo } = useOnboardingStore();
  const { loadAuthStateFromStorage } = useAuthStore();
  const [countryTouched, setCountryTouched] = useState(false);
  const [bizTypeTouched, setBizTypeTouched] = useState(false);
  const {
    getBusinessTypes,
    loading: loadingBizStore,
    businessTypes,
  } = useBusinessStore();
  const authState = loadAuthStateFromStorage();
  const { email } = authState || {};

  const formik = useFormik({
    initialValues: {
      businessName: '',
      email: businessInfo.email || email || '',
      description: '',
      website: '',
      businessType: businessInfo.businessType || '',
      countryCode: '',
      countryName: '',
      countryId: '',
      relationshipEstablishedAt: null,
    },
    enableReinitialize: true,
    validationSchema: kybUpdateValidationSchemas[0],
    validateOnMount: true,
    onSubmit: () => {},
  });

  const filteredCountries = countries.filter(
    (country) =>
      country.name === 'Nigeria' ||
      country.name === 'Ghana' ||
      country.name === 'Senegal' ||
      country.name === 'Cameroon',
  );

  const handleBusinessTypeChange = (value: string) => {
    formik.setFieldValue('businessType', value);
    setBusinessType(value);
  };

  const handleRelationshipDateChange = (date: Date | null) => formik.setFieldValue('relationshipEstablishedAt', date);

  const handleRelationshipDateBlur = (date: Date | null) => {
    if (isFutureDate(date)) {
      formik.setFieldValue('relationshipEstablishedAt', null);
      return toast.error('Date cannot be in the future');
    }
  };

  useEffect(() => {
    getBusinessTypes();
    getActiveCountries();
  }, []);

  return (
    <div className="p-4 rounded-xl gap-8 flex flex-col border border-[#F1F2F4] w-full h-full bg-white ">
      <div className="grid gap-6">
        <p className="ryno_text_semibold">Tell us about your business</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Business Name */}
          <div>
            <AppInput
              type="text"
              name="businessName"
              id="businessName"
              onChange={formik.handleChange}
              placeholder="Enter your business name"
              required
              label="What is your business name?"
              onBlur={formik.handleBlur}
              value={formik.values.businessName}
              errorMessage={
                formik.touched.businessName && formik.errors.businessName
                  ? formik.errors.businessName
                  : ''
              }
            />
          </div>

          {/* Email Address */}
          <div>
            <AppInput
              type="text"
              name="email"
              id="email"
              onChange={formik.handleChange}
              placeholder="Enter your email address"
              required
              label="What is your email address?"
              onBlur={formik.handleBlur}
              value={formik.values.email}
              errorMessage={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : ''
              }
            />
          </div>

          {/* Business Type */}
          <div>
            <CustomDropdown
              label={'Select your business type'}
              options={businessTypes.map((type: any) => {
                return {
                  name: type,
                  label: type,
                };
              })}
              selectedValue={formik.values.businessType}
              onSelectChange={(value) => {
                handleBusinessTypeChange(value);
                setBizTypeTouched(true);
              }}
              loading={loadingBizStore}
              required
              errorMessage={
                !formik.values.businessType && bizTypeTouched
                  ? 'Business type is required'
                  : ''
              }
            />
          </div>

          {/* Country */}
          <div>
            <CustomDropdown
              label="Select Country"
              options={filteredCountries.map((country: IActiveCountry) => {
                return {
                  name: country.iso3,
                  label: country.name,
                  countryFlag: country.flagUrl,
                  id: country.id,
                };
              })}
              selectedValue={formik.values.countryCode}
              onSelectChange={(value) => {
                formik.setFieldValue('countryCode', value.name);
                formik.setFieldValue('countryName', value.label);
                formik.setFieldValue('countryId', value.id);
                getAllCountryStates(value.name);
                setCountryTouched(true);
              }}
              emitObject
              required
              showCountryFlag
              errorMessage={
                !formik.values.countryCode && countryTouched
                  ? 'Country is required'
                  : ''
              }
            />
          </div>

          {/* Website */}
          <div>
            <AppInput
              type="text"
              name="website"
              id="website"
              onChange={formik.handleChange}
              placeholder="Enter your website URL e.g., http://example.com"
              label="Does your business have a website?"
              required
              onBlur={formik.handleBlur}
              value={formik.values.website}
              errorMessage={
                formik.touched.website && formik.errors.website
                  ? formik.errors.website
                  : ''
              }
            />
          </div>

          {/* Relationship Established Date */}
          <div>
            <AppDatePicker
              label="Relationship Established At"
              placeholder='Type "yyyy-mm-dd" or select from calendar'
              required
              value={formik.values.relationshipEstablishedAt}
              onChange={(date) => handleRelationshipDateChange(date)}
              onBlur={(date) => handleRelationshipDateBlur(date)}
            />
          </div>

          {/* Business Description */}
          <div className="col-span-2">
            <AppTextArea
              name="description"
              id="description"
              label="Describe your business"
              placeholder="Max 1000 characters"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              errorMessage={
                formik.touched.description && formik.errors.description
                  ? formik.errors.description
                  : ''
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutBusiness;
